import { indexOrDie, lastOrDie, BigNumber, mustNeverExist } from "@repo/utils";

export type PositionSplitPart = {
    quantity: BigNumber;
    scheduleIdx: number;
    confirmationIdx: number | null;
    split: boolean;
};

export type PositionSplitParts = Array<PositionSplitPart>;

/**
 * andrei: If the function returns more than one sub-array, it means new positions must be created for idx > 0.
 * andrei: Assume schedules has at least one element always.
 */
export function calculatePositionSplits(
    schedules: Array<{ quantity: BigNumber | number }>,
    confirmations: Array<{ quantity: BigNumber | number | null }>,
    nextQuantity: number,
    splitConfirmationToPositions: boolean,
): PositionSplitParts[] {
    let totalBudget = BigNumber(nextQuantity);
    let scheduleLineBudget: BigNumber | null = null;
    let confirmationBudget: BigNumber | null;

    let noMoreSchedules = false;
    let scheduleIdx: number = 0;
    let confirmationIdx: number | null = confirmations.length > 0 ? 0 : null;

    function nextSchedule(args: { action: "addPart" | "pushIndexForward" | "preserveLastPart" }) {
        if (scheduleIdx + 1 >= schedules.length) {
            noMoreSchedules = true;
        } else {
            scheduleIdx++;
        }
        scheduleLineBudget = !noMoreSchedules ? BigNumber(indexOrDie(schedules, scheduleIdx).quantity) : null;
        switch (args.action) {
            case "addPart":
                if (scheduleLineBudget !== null) {
                    lastOrDie(result).push({
                        scheduleIdx,
                        quantity: BigNumber(0),
                        confirmationIdx,
                        split: false,
                    });
                    lastPositionScheduleLine = lastOrDie(lastOrDie(result));
                }
                break;
            case "pushIndexForward":
                lastPositionScheduleLine.scheduleIdx = scheduleIdx;
                break;
            case "preserveLastPart":
                break;
            default:
                throw mustNeverExist(args.action);
        }
    }

    function nextConfirmation(args: { forceNewPosition: boolean; forceNewPart: boolean; split: boolean }) {
        confirmationIdx =
            confirmationIdx !== null && confirmationIdx + 1 < confirmations.length ? confirmationIdx + 1 : null;
        confirmationBudget =
            confirmationIdx !== null ? BigNumber(indexOrDie(confirmations, confirmationIdx).quantity ?? 0) : null;

        if (args.forceNewPart || confirmationBudget !== null) {
            if (args.forceNewPosition) {
                result.push([]);
            }
            lastOrDie(result).push({
                scheduleIdx,
                quantity: BigNumber(0),
                confirmationIdx,
                split: args.split,
            });
            lastPositionScheduleLine = lastOrDie(lastOrDie(result));
            return true;
        }
        return false;
    }

    const result: PositionSplitParts[] = [
        [
            {
                scheduleIdx,
                quantity: BigNumber(0),
                confirmationIdx,
                split: false,
            },
        ],
    ];
    let lastPositionScheduleLine = lastOrDie(lastOrDie(result));

    scheduleLineBudget = BigNumber(indexOrDie(schedules, scheduleIdx).quantity);
    confirmationBudget =
        confirmationIdx !== null ? BigNumber(indexOrDie(confirmations, confirmationIdx).quantity ?? 0) : null;

    while (totalBudget.gt(0) && (confirmationBudget !== null || scheduleLineBudget !== null)) {
        if (confirmationBudget === null) {
            const value = totalBudget.gt(scheduleLineBudget) ? scheduleLineBudget : totalBudget;
            lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(value);
            totalBudget = totalBudget.minus(value);
            nextSchedule({ action: totalBudget.gt(0) ? "addPart" : "preserveLastPart" });
        } else if (scheduleLineBudget === null) {
            lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(confirmationBudget);
            totalBudget = totalBudget.minus(confirmationBudget);
            nextConfirmation({
                forceNewPosition: splitConfirmationToPositions,
                forceNewPart: false,
                split: false,
            });
        } else if (scheduleLineBudget.gt(confirmationBudget)) {
            lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(confirmationBudget);
            scheduleLineBudget = scheduleLineBudget.minus(confirmationBudget);
            totalBudget = totalBudget.minus(confirmationBudget);
            nextConfirmation({
                forceNewPosition: splitConfirmationToPositions,
                forceNewPart: totalBudget.gt(0),
                split: true,
            });
        } else if (scheduleLineBudget.lt(confirmationBudget)) {
            lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(scheduleLineBudget);
            confirmationBudget = confirmationBudget.minus(scheduleLineBudget);
            totalBudget = totalBudget.minus(scheduleLineBudget);
            nextSchedule({ action: totalBudget.gt(0) ? "addPart" : "preserveLastPart" });
        } else {
            lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(scheduleLineBudget);
            totalBudget = totalBudget.minus(scheduleLineBudget);
            const partInserted = nextConfirmation({
                forceNewPosition: splitConfirmationToPositions,
                forceNewPart: totalBudget.gt(0),
                split: false,
            });
            nextSchedule({
                action: totalBudget.gt(0) ? (partInserted ? "pushIndexForward" : "addPart") : "preserveLastPart",
            });
        }
    }

    if (totalBudget.gt(0)) {
        lastPositionScheduleLine.quantity = lastPositionScheduleLine.quantity.plus(totalBudget);
    }

    return result;
}
